type FileSystemEntryLike = {
    isFile: boolean;
    isDirectory: boolean;
    file: (success: (file: File) => void, error?: () => void) => void;
    createReader: () => { readEntries: (success: (entries: FileSystemEntryLike[]) => void, error?: () => void) => void };
};

const EXTENSIONS_BY_MIME: Record<string, string[]> = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/jpg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/gif': ['.gif'],
};

function readDirectoryEntries(reader: { readEntries: (success: (entries: FileSystemEntryLike[]) => void, error?: () => void) => void }): Promise<FileSystemEntryLike[]> {
    return new Promise((resolve) => {
        const allEntries: FileSystemEntryLike[] = [];

        const readBatch = () => {
            reader.readEntries(
                (entries) => {
                    if (entries.length === 0) {
                        resolve(allEntries);
                        return;
                    }
                    allEntries.push(...entries);
                    readBatch();
                },
                () => resolve(allEntries),
            );
        };

        readBatch();
    });
}

async function readEntry(entry: FileSystemEntryLike): Promise<File[]> {
    if (entry.isFile) {
        return new Promise((resolve) => {
            entry.file(
                (file) => resolve([file]),
                () => resolve([]),
            );
        });
    }

    if (entry.isDirectory) {
        try {
            const reader = entry.createReader();
            const entries = await readDirectoryEntries(reader);
            const nested = await Promise.all(entries.map((child) => readEntry(child)));
            return nested.flat();
        } catch {
            return [];
        }
    }

    return [];
}

/** OS'ten sürüklenen dosya ve klasörleri (webkitGetAsEntry) File[] olarak toplar. */
export async function collectFilesFromDropEvent(event: Event): Promise<File[]> {
    const dragEvent = event as DragEvent;
    const items = dragEvent.dataTransfer?.items;
    if (!items?.length) {
        return [];
    }

    const droppedFiles: File[] = [];
    const promises: Promise<File[]>[] = [];

    for (const item of Array.from(items)) {
        if (item.kind !== 'file') {
            continue;
        }

        const webkitItem = item as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntryLike | null };
        if (typeof webkitItem.webkitGetAsEntry === 'function') {
            const entry = webkitItem.webkitGetAsEntry();
            if (entry) {
                promises.push(readEntry(entry));
                continue;
            }
        }

        const file = item.getAsFile();
        if (file) {
            droppedFiles.push(file);
        }
    }

    if (promises.length > 0) {
        const filesArrays = await Promise.all(promises);
        droppedFiles.push(...filesArrays.flat());
    }

    return droppedFiles;
}

/** react-dropzone v11 accept prop — string veya undefined. */
export function normalizeDropzoneAccept(accept?: string): string | undefined {
    if (!accept?.trim()) {
        return undefined;
    }

    const normalized = accept.trim();
    if (normalized === '*/*' || normalized === '*') {
        return undefined;
    }

    return normalized;
}

export function filterFilesByAccept(files: File[], accept?: string): File[] {
    const normalized = accept?.trim();
    if (!normalized || normalized === '*/*' || normalized === '*') {
        return files;
    }

    const mimeOrExtParts = normalized.split(',').map((part) => part.trim()).filter(Boolean);
    const allowedMimes = new Set<string>();
    const allowedExtensions = new Set<string>();

    for (const part of mimeOrExtParts) {
        if (part.startsWith('.')) {
            allowedExtensions.add(part.toLowerCase());
            continue;
        }
        allowedMimes.add(part);
        for (const ext of EXTENSIONS_BY_MIME[part] ?? []) {
            allowedExtensions.add(ext);
        }
    }

    return files.filter((file) => {
        if (file.type && allowedMimes.has(file.type)) {
            return true;
        }
        if (file.type === 'image/jpeg' && (allowedMimes.has('image/jpeg') || allowedMimes.has('image/jpg'))) {
            return true;
        }
        const dot = file.name.lastIndexOf('.');
        if (dot >= 0) {
            return allowedExtensions.has(file.name.slice(dot).toLowerCase());
        }
        return false;
    });
}

/** @deprecated v14 object format; use normalizeDropzoneAccept for react-dropzone v11 */
export function parseAcceptString(accept?: string): string | undefined {
    return normalizeDropzoneAccept(accept);
}
